import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  clientApiService,
  ConversationItem,
} from "../../services/client-api.service";

function formatRelativeTime(t: number | null) {
  if (!t) return "";
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (min < 1) return "Ahora";
  if (min < 60) return `Hace ${min} min`;
  if (hours < 24) return `Hace ${hours} h`;
  if (days < 7) return `Hace ${days} d`;
  return new Date(t).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
  });
}

export function MessagesListPage() {
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await clientApiService.listConversations();
      setConversations(data);
    } catch (err: any) {
      setError(err?.message || "Error al cargar conversaciones");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
        {error}
        <button onClick={load} className="ml-3 underline font-medium">
          Reintentar
        </button>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
        <div className="text-5xl mb-3 opacity-40">💬</div>
        <h3 className="text-lg font-semibold text-slate-900">
          Aún no tienes conversaciones
        </h3>
        <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
          Cuando el despacho te asigne un proyecto con arquitecto líder,
          podrás comunicarte con él desde aquí.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Mensajes</h2>
          <p className="text-sm text-slate-500 mt-1">
            Comunícate con tu arquitecto líder
          </p>
        </div>
        <button
          onClick={load}
          disabled={isLoading}
          className="text-sm bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-lg font-medium transition disabled:opacity-50"
        >
          🔄 Actualizar
        </button>
      </div>

      <ul className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
        {conversations.map((conv) => (
          <li key={conv.projectId}>
            <Link
              to={`/dashboard/messages/${conv.projectId}`}
              className="block p-4 hover:bg-slate-50 transition"
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-primary-light text-primary flex items-center justify-center font-bold shrink-0">
                  {conv.projectLeadName?.charAt(0).toUpperCase() ?? "💬"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className="font-semibold text-slate-900 truncate">
                      {conv.projectLeadName || "Arquitecto"}
                    </p>
                    <span className="text-xs text-slate-400 shrink-0">
                      {formatRelativeTime(conv.lastMessageAt)}
                    </span>
                  </div>
                  <p className="text-xs text-primary font-medium truncate mb-1">
                    {conv.projectName}
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-slate-500 truncate flex-1">
                      {conv.lastMessagePreview || "Sin mensajes aún"}
                    </p>
                    {conv.unreadCount > 0 && (
                      <span className="bg-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
                        {conv.unreadCount > 9 ? "9+" : conv.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}